import React, { useState, useEffect, useRef, useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from "@mui/material";
import Styles from "../../../styles/vendorDashboard.module.css";
import MenuIcon from "@mui/icons-material/Menu";
import {
  useMediaQuery,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Menu,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  DialogActions,
  useTheme,
  Tooltip,
  CircularProgress,
  IconButton,
  Pagination,
  TextField,
  InputAdornment,
  Grid,
} from "@mui/material";
import Swal from "sweetalert2";
import axios from "axios";
import RefreshIcon from "@mui/icons-material/Refresh";
import styles from "../../../styles/adminPortal.module.css";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ViewDeliveryCenterModal from "./viewDeliveryCenterModal";
import GlobalDropdownField from "../../../shared/utils/GlobalDropdownField";
import GlobalButton from "../../../shared/utils/GlobalButton";

const HEAD_CELLS = [
  { id: "deliveryCenter", label: "Delivery Center", width: 200 },
  { id: "stateName", label: "State", width: 200 },
  { id: "status", label: "Status", width: 150 },
  { id: "addedBy", label: "Added By", width: 160 },
  { id: "updatedBy", label: "Updated By", width: 150 },
  { id: "linkedCities", label: "Linked Cities", width: 120 },
  { id: "action", label: "Action", width: 60 },
];

// ─── Table Head ───────────────────────────────────────────────────────────────
function EnhancedTableHead({
  searchInputs,
  handleSearchChangeHead,
  tableLoading,
  filteredCount,
}) {
  const handleTextChange = (key) => (e) => {
    handleSearchChangeHead(e, key);
  };

  const hasActiveFilters = useMemo(() => {
    return (
      (searchInputs?.search && searchInputs.search.trim() !== "") ||
      (searchInputs?.states && searchInputs.states.trim() !== "") ||
      (searchInputs?.status && searchInputs.status.trim() !== "")
    );
  }, [searchInputs]);

  return (
    <TableHead className={styles.tableHead}>
      <TableRow>
        {HEAD_CELLS.map((cell) => (
          <TableCell
            key={cell.id}
            className={styles.tableCell}
            sx={{ minWidth: cell.width }}
          >
            <span className={styles.headCellHeading}>{cell.label}</span>
            <div className={styles.topBorderDiv}>
              {cell.id === "deliveryCenter" && (
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder="Search city..."
                  onChange={handleTextChange("search")}
                  value={searchInputs?.search || ""}
                />
              )}
              {cell.id === "stateName" && (
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder="Search state..."
                  onChange={handleTextChange("states")}
                  value={searchInputs?.states || ""}
                />
              )}
              {cell.id === "status" && (
                <GlobalDropdownField
                  variant="searchDropdown"
                  name="statusFilter"
                  value={searchInputs?.status || ""}
                  onChange={(value) => handleSearchChangeHead(value, "status")}
                  placeholder="Select Status"
                  options={[
                    { value: "", label: "All Status" },
                    { value: "active", label: "Active" },
                    { value: "inactive", label: "Inactive" },
                  ]}
                  renderValueFn={(selected) => {
                    const labels = {
                      active: "Active",
                      inactive: "Inactive",
                    };
                    return labels[selected] || "All Status";
                  }}
                />
              )}
            </div>
          </TableCell>
        ))}
      </TableRow>
      {!tableLoading && filteredCount > 0 && hasActiveFilters && (
        <TableRow sx={{ lineHeight: "14px" }}>
          <TableCell
            colSpan={HEAD_CELLS.length}
            sx={{
              backgroundColor: "#DEEBFF",
              position: "relative",
              overflow: "hidden",
              lineHeight: "16px",
            }}
          >
            <div className="h-full w-full flex justify-center items-center">
              {`${filteredCount} Delivery Centers`}
            </div>
          </TableCell>
        </TableRow>
      )}
    </TableHead>
  );
}

// ─── Toolbar ──────────────────────────────────────────────────────────────────
function EnhancedTableToolbar({
  totalDeliveryCenters,
  onRefresh,
  onCreateClick,
}) {
  const [rotate, setRotate] = useState(false);
  const [mobileMenuOpen, setMobile] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleRefresh = () => {
    setRotate(true);
    onRefresh();
    setMobile(false);
    setTimeout(() => setRotate(false), 500);
  };

  const handleCreate = () => {
    onCreateClick();
    setMobile(false);
  };

  return (
    <div className="border-b flex justify-between items-center h-14 pt-2">
      {!isMobile ? (
        <>
          <div className="font-bold text-md text-gray-600">
            Total Delivery Centers: {totalDeliveryCenters}
          </div>
          <div className="flex">
            <Tooltip title="Refresh">
              <IconButton
                onClick={handleRefresh}
                sx={{
                  animation: rotate
                    ? "rotateAnimation 0.5s ease-in-out"
                    : "none",
                  "@keyframes rotateAnimation": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Create Delivery Center">
              <IconButton onClick={handleCreate}>
                <AddIcon sx={{ color: "#666666" }} />
              </IconButton>
            </Tooltip>
          </div>
        </>
      ) : (
        <div className="flex justify-between items-center w-full">
          <Typography sx={{ flex: "1 1 100%" }} component="div">
            Total Delivery Centers: {totalDeliveryCenters}
          </Typography>
          <IconButton color="inherit" onClick={() => setMobile(true)}>
            <MenuIcon />
          </IconButton>
        </div>
      )}

      <Dialog
        open={mobileMenuOpen}
        onClose={() => setMobile(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle className="flex justify-between items-center">
          <span>Actions</span>
          <IconButton onClick={() => setMobile(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div
            className="flex items-center border mb-2 rounded-md p-2 hover:bg-gray-50 cursor-pointer"
            onClick={handleRefresh}
          >
            <IconButton>
              <RefreshIcon />
            </IconButton>
            <p className="ml-2 font-medium">Refresh</p>
          </div>
          <div
            className="flex items-center border mb-2 rounded-md p-2 hover:bg-gray-50 cursor-pointer"
            onClick={handleCreate}
          >
            <IconButton>
              <AddIcon />
            </IconButton>
            <p className="ml-2 font-medium">Add Delivery Center</p>
          </div>
        </DialogContent>
        <DialogActions>
          <GlobalButton variant="secondary" onClick={() => setMobile(false)}>
            {`Close`}
          </GlobalButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const DeliveryCenters = () => {
  const [deliveryCenters, setDeliveryCenters] = useState([]);
  const [totalDeliveryCenters, setTotalDeliveryCenters] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [5, 10, 50, 100, 200];
  const [filteredCount, setFilteredCount] = useState(0);

  const [searchInputs, setSearchInputs] = useState({
    search: "",
    states: "",
    status: "",
  });

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);

  // Modal state: null editData → create mode; object → edit mode
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const serverURL = process.env.REACT_APP_URL;
  const token = localStorage.getItem("token");
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  // ── Fetch list ─────────────────────────────────────────────────────────────
  // The API now returns each DC with linkedCities: [{ _id, cityName, stateName }]
  const openCreateModal = () => {
    setEditData(null);
    setModalOpen(true);
  };

  const openEditModal = (row) => {
    setEditData(row);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (isSaving) return;
    setModalOpen(false);
    setEditData(null);
  };

  const fetchTotalCount = async () => {
    try {
      const res = await axios.get(
        `${serverURL}/deliveryCenter/viewAll?page=1&limit=1`,
        authHeader,
      );
      if (res?.data?.success) {
        setTotalDeliveryCenters(res?.data?.totalRecords || 0);
      }
    } catch (err) {
      console.error("Error fetching total count:", err);
    }
  };

  const fetchDeliveryCenters = async (
    pg = page,
    limit = pageSize,
    filters = searchInputs,
  ) => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      params.append("page", pg);
      params.append("limit", limit);

      if (filters.search) params.append("search", filters.search);
      if (filters.states) params.append("states", filters.states);
      if (filters.status) params.append("status", filters.status);

      const res = await axios.get(
        `${serverURL}/deliveryCenter/viewAll?${params.toString()}`,
        authHeader,
      );

      if (res?.data?.success) {
        setDeliveryCenters(res?.data?.data || []);
        setFilteredCount(res?.data?.totalRecords || 0);
        setTotalPages(res?.data?.totalPages || 1);
      }
    } catch (err) {
      console.error("Error fetching Delivery Centers:", err);
      setDeliveryCenters([]);
      setFilteredCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTotalCount();
    fetchDeliveryCenters(1, pageSize, searchInputs);
  }, []);

  const handleSearchChangeHead = (e, key) => {
    let value;

    if (e?.target) {
      value = e.target.value;
    } else {
      value = e;
    }

    if (value?.target) {
      value = value.target.value;
    }

    // Only transform search to lowercase
    if (key === "search" && typeof value === "string") {
      value = value.toLowerCase();
    }

    setSearchInputs((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchDeliveryCenters(1, pageSize, searchInputs);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInputs]);

  // ── API error normalization helper ─────────────────────────────────────────
  const normalizeApiError = (error, fallbackMessage) => {
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    if (error?.response?.data?.error) {
      return error.response.data.error;
    }
    if (error?.message) {
      return error.message;
    }
    return fallbackMessage;
  };

  const apiCreateDC = async (dcFields) => {
    try {
      const res = await axios.post(
        `${serverURL}/deliveryCenter/create`,
        dcFields,
        authHeader,
      );
      if (!res?.data?.success) {
        throw new Error(normalizeApiError({ response: res }, "Create failed"));
      }
      return res?.data?.data; // returns { _id, cityName, stateName, … }
    } catch (err) {
      throw new Error(normalizeApiError(err, "Create failed"));
    }
  };

  // ── API: Update DC ─────────────────────────────────────────────────────────
  const apiUpdateDC = async (id, dcFields) => {
    try {
      const res = await axios.put(
        `${serverURL}/deliveryCenter/update/${id}`,
        dcFields,
        authHeader,
      );
      if (!res?.data?.success) {
        throw new Error(normalizeApiError({ response: res }, "Update failed"));
      }
      return res?.data?.data;
    } catch (err) {
      throw new Error(normalizeApiError(err, "Update failed"));
    }
  };

  // ── API: Soft-delete DC ────────────────────────────────────────────────────
  // Also cascades soft-delete to all linked city rows (handled backend-side).
  const apiDeleteDC = async (id) => {
    try {
      const res = await axios.delete(
        `${serverURL}/deliveryCenter/delete/${id}`,
        authHeader,
      );
      if (!res?.data?.success) {
        throw new Error(normalizeApiError({ response: res }, "Delete failed"));
      }
    } catch (err) {
      throw new Error(normalizeApiError(err, "Delete failed"));
    }
  };

  // ── API: Link one city to a DC ─────────────────────────────────────────────
  // POST /deliveryCenter/:deliveryCenterId/linkCity
  //   body: { cityName, stateName, coordinates?: { latitude, longitude } }
  // 409 means "already linked" — silent no-op (backend returns success:true).
  const apiLinkCity = async (dcId, cityName, stateName, coordinates) => {
    try {
      const res = await axios.post(
        `${serverURL}/deliveryCenter/linkDeliveryCenterToCity/${dcId}`,
        { cityName, stateName, ...(coordinates ? { coordinates } : {}) },
        authHeader,
      );
      if (!res?.data?.success) {
        throw new Error(
          res?.data?.message || `Failed to link city "${cityName}".`,
        );
      }
      return res?.data?.data;
    } catch (err) {
      if (err?.response?.status === 409) {
        throw new Error(
          `City "${cityName}" is already linked to another delivery center.`,
        );
      }
      throw new Error(
        normalizeApiError(err, `Failed to link city "${cityName}"`),
      );
    }
  };

  // ── API: Unlink one city (by link document _id) ────────────────────────────
  const apiUnlinkCity = async (linkId) => {
    try {
      const res = await axios.patch(
        `${serverURL}/deliveryCenter/unlinkDeliveryCenterFromCity/${linkId}`,
        {},
        authHeader,
      );
      if (!res?.data?.success) {
        throw new Error(normalizeApiError({ response: res }, "Unlink failed"));
      }
      return res?.data?.data;
    } catch (err) {
      if (err.response?.status === 404) {
        console.warn(`Link ${linkId} not found, may have been already removed`);
        return null;
      }
      throw new Error(normalizeApiError(err, "Unlink failed"));
    }
  };

  // ── Sync linked cities: diff old vs new ───────────────────────────────────
  /**
   * dcId             — the DC's _id
   * stateName        — DC's state (used for newly-linked city rows)
   * oldLinks         — [{ _id, cityName, stateName, coordinates }] from viewAll
   * newCityNames     — string[] of city names the user wants after save
   * linkedCityCoords — { [cityName]: { latitude, longitude } } from modal
   *
   * Adds cities in newCityNames not in oldLinks (with coordinates).
   * Removes link docs whose cityName is no longer in newCityNames.
   * All calls run in parallel.
   */
  const syncLinkedCities = async (
    dcId,
    stateName,
    oldLinks,
    newCityNames,
    linkedCityCoords = {},
  ) => {
    const oldNameSet = new Set(oldLinks?.map((l) => l.cityName));
    const newNameSet = new Set(newCityNames);

    const toLink = newCityNames.filter((name) => !oldNameSet.has(name));
    const toUnlink = oldLinks?.filter((l) => !newNameSet.has(l?.cityName));

    const validToUnlink = toUnlink.filter((link) => link?._id);

    for (const cityName of toLink) {
      try {
        await apiLinkCity(
          dcId,
          cityName,
          stateName,
          linkedCityCoords[cityName] || null,
        );
      } catch (err) {
        if (err.response?.status === 409) {
          console.warn(`City "${cityName}" already linked, skipping`);
          continue;
        }
        throw err;
      }
    }

    for (const link of validToUnlink) {
      try {
        await apiUnlinkCity(link._id);
      } catch (err) {
        if (err.response?.status === 404) {
          console.warn(`Link ${link._id} already removed, skipping`);
          continue;
        }
        throw err;
      }
    }
  };

  const handleSave = async (payload) => {
    const {
      linkedCities: newCityNames,
      linkedCityCoords = {},
      ...dcFields
    } = payload;
    const isEdit = Boolean(editData?._id);

    setIsSaving(true);
    try {
      // Step 1 — create or update the DC document (coordinates included in dcFields)
      const savedDC = isEdit
        ? await apiUpdateDC(editData?._id, dcFields)
        : await apiCreateDC(dcFields);

      const dcId = isEdit ? editData?._id : savedDC._id;
      const stateName = dcFields.stateName;

      // Step 2 — diff and sync linked cities
      // oldLinks is [] on create; on edit it's the populated array from viewAll.
      const oldLinks = isEdit ? editData?.linkedCities || [] : [];
      await syncLinkedCities(
        dcId,
        stateName,
        oldLinks,
        newCityNames,
        linkedCityCoords,
      );

      Swal.fire({
        icon: "success",
        title: isEdit ? "Delivery Center Updated" : "Delivery Center Created",
        timer: 1500,
        showConfirmButton: false,
      });

      setModalOpen(false);
      setEditData(null);
      fetchTotalCount();
      fetchDeliveryCenters(page, pageSize, searchInputs);
    } catch (err) {
      console.error("Save error:", err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (e, row) => {
    e.stopPropagation();
    handleMenuClose();

    const result = await Swal.fire({
      icon: "warning",
      title: "Delete Delivery Center?",
      text: `"${row.cityName}" and all its linked cities will be removed.`,
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete",
    });
    if (!result.isConfirmed) return;

    try {
      await apiDeleteDC(row._id);
      Swal.fire({
        icon: "success",
        title: "Deleted!",
        timer: 1200,
        showConfirmButton: false,
      });
      fetchTotalCount();
      fetchDeliveryCenters(page, pageSize, searchInputs);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Delete failed", text: err.message });
    }
  };

  // ── Row menu helpers ───────────────────────────────────────────────────────
  const handleMenuOpen = (e, id) => {
    setAnchorEl(e.currentTarget);
    setSelectedRowId(id);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowId(null);
  };

  // ── Pagination ─────────────────────────────────────────────────────────────
  const handlePageChange = (_, val) => {
    setPage(val);
    fetchDeliveryCenters(val, pageSize, searchInputs);
  };
  const handlePageSizeChange = (event) => {
    const size = Number(event.target.value);
    setPageSize(size);
    setPage(1);
    fetchDeliveryCenters(1, size, searchInputs);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading && deliveryCenters.length === 0)
    return (
      <div className={styles.tableLoading}>
        <CircularProgress />
      </div>
    );

  return (
    <div className={styles.tableGround}>
      <Box>
        <Paper
          sx={{ width: "100%", boxShadow: "none" }}
          className={styles.tableGap}
        >
          <EnhancedTableToolbar
            totalDeliveryCenters={totalDeliveryCenters}
            onRefresh={() => {
              fetchTotalCount();
              fetchDeliveryCenters(page, pageSize, searchInputs);
            }}
            onCreateClick={openCreateModal}
          />

          <TableContainer
            className={`${styles.tableContainer} ${Styles["custom-scrollbar"]}`}
          >
            <Table stickyHeader className="rounded-lg">
              <EnhancedTableHead
                searchInputs={searchInputs}
                handleSearchChangeHead={handleSearchChangeHead}
                tableLoading={loading}
                filteredCount={filteredCount}
              />

              {deliveryCenters.length === 0 ? (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={HEAD_CELLS.length}>
                      <div className="flex justify-center items-center h-[70vh]">
                        {loading ? (
                          <CircularProgress />
                        ) : (
                          "No Delivery Centers found"
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <TableBody>
                  {deliveryCenters.map((v) => {
                    // linkedCities is now [{ _id, cityName, stateName }] from the API
                    const links = Array.isArray(v?.linkedCities)
                      ? v?.linkedCities
                      : [];
                    const linkedCount = links.length;
                    const linkedNames = links.map((l) => l.cityName).join(", ");

                    return (
                      <TableRow
                        hover
                        key={v?._id}
                        className={styles.tableRowHead}
                        tabIndex={-1}
                      >
                        <TableCell className={styles.tableRow}>
                          <Tooltip title={v?.cityName} arrow>
                            <span className={styles.truncateText}>
                              {v?.cityName || "—"}
                            </span>
                          </Tooltip>
                        </TableCell>

                        <TableCell className={styles.tableRow}>
                          <span>{v?.stateName || "—"}</span>
                        </TableCell>

                        <TableCell className={styles.tableRow}>
                          <Chip
                            label={v?.isActive ? "Active" : "Inactive"}
                            size="small"
                            color={v?.isActive ? "success" : "default"}
                            variant="outlined"
                          />
                        </TableCell>

                        <TableCell className={styles.tableRow}>
                          <span>{v?.addedBy?.name || "—"}</span>
                        </TableCell>

                        <TableCell className={styles.tableRow}>
                          <span>{v?.updatedBy?.name || "—"}</span>
                        </TableCell>

                        {/* Linked Cities: count chip + tooltip listing all names */}
                        <TableCell className={styles.tableRow}>
                          <Tooltip
                            title={
                              linkedCount > 0 ? linkedNames : "No cities linked"
                            }
                            arrow
                          >
                            <Chip
                              label={linkedCount}
                              size="small"
                              color={linkedCount > 0 ? "primary" : "default"}
                              variant="outlined"
                              sx={{ cursor: "default" }}
                            />
                          </Tooltip>
                        </TableCell>

                        <TableCell
                          className={styles.tableRow}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <IconButton
                            onClick={(e) => handleMenuOpen(e, v?._id)}
                          >
                            <MoreVertIcon
                              style={{ color: "var(--primary-color)" }}
                            />
                          </IconButton>
                          <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl) && selectedRowId === v?._id}
                            onClose={handleMenuClose}
                            keepMounted
                          >
                            <MenuItem
                              onClick={() => {
                                handleMenuClose();
                                openEditModal(v);
                              }}
                            >
                              <EditOutlinedIcon style={{ color: "#6b7280" }} />
                              <p className="ml-2 font-semibold text-[#6b7280]">
                                Edit
                              </p>
                            </MenuItem>
                            <MenuItem onClick={(e) => handleDelete(e, v)}>
                              <DeleteOutlinedIcon style={{ color: "red" }} />
                              <p className="ml-2 font-semibold text-[red]">
                                Delete
                              </p>
                            </MenuItem>
                          </Menu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              )}
            </Table>
          </TableContainer>

          <hr className="mt-2 bg-gray-200" />

          <div className="flex items-center justify-between">
            <FormControl
              variant="standard"
              size="small"
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 1,
                "& .MuiInputBase-root:before": { borderBottom: "none" },
                "& .MuiInputBase-root:after": { borderBottom: "none" },
                "& .MuiInputBase-root": { marginTop: 1 },
              }}
            >
              <InputLabel
                id="page-size-label"
                sx={{
                  position: "static",
                  transform: "none",
                  fontSize: "14px",
                  marginRight: "8px",
                  marginTop: "2px",
                }}
              >
                Rows
              </InputLabel>
              <Select
                labelId="page-size-label"
                value={pageSize}
                onChange={handlePageSizeChange}
                label="Rows"
              >
                {pageSizeOptions.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Pagination
              className="p-2 flex justify-center"
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              sx={{
                "& .MuiPaginationItem-root.Mui-selected": {
                  backgroundColor: "#418cff",
                  color: "#fff",
                },
                "& .MuiPaginationItem-root.Mui-selected:hover": {
                  backgroundColor: "#3366cc",
                },
                "& .MuiPaginationItem-root": {
                  "&:hover": { backgroundColor: "#ddd" },
                },
              }}
            />
          </div>
        </Paper>
      </Box>

      {/* Single modal — handles both Create and Edit */}
      <ViewDeliveryCenterModal
        open={modalOpen}
        editData={editData}
        onClose={closeModal}
        onSave={handleSave}
        isSaving={isSaving}
      />
    </div>
  );
};

export default DeliveryCenters;
